// OrdersView.js
import React, { useState } from 'react';
import { 
  Search, Filter, Download, Calendar, MapPin, Clock, 
  Mail, Phone, CreditCard, Package
} from 'lucide-react';

const OrdersView = ({ orders, updateOrderStatus }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Filter and sort orders
  const filteredOrders = orders?.filter(order => {
    const matchesSearch = (
      order?.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order?.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order?.streetAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order?.zipCode?.includes(searchTerm) ||
      order?._id?.toString().includes(searchTerm)
    );
    const matchesFilter = filterStatus === 'all' || order?.status === filterStatus;
    return matchesSearch && matchesFilter;
  }) || [];

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Format address for display
  const formatAddress = (order) => {
    return `${order.streetAddress}${order.apartmentUnit ? ', ' + order.apartmentUnit : ''}, ${order.city}, FL ${order.zipCode}`;
  };

  return (
    <div className="bg-amber-50 rounded-lg shadow-lg p-6">
      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
            />
          </div>
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
        >
          <option value="all">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Confirmed">Confirmed</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
        </select>

        <button
          onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
          className="px-4 py-2 border rounded-md hover:bg-rose-50"
        >
          {sortOrder === 'desc' ? '↓ Desc' : '↑ Asc'}
        </button>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.map((order) => (
          <div
            key={order._id}
            className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow"
            onClick={() => setSelectedOrder(order)}
          >
            <div className="flex flex-wrap justify-between items-start gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-rose-800" />
                  <span className="font-semibold">Order #{order._id}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(order.deliveryDate)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="h-4 w-4" />
                  <span>{formatAddress(order)}</span>
                </div>
              </div>

              <div className="space-y-2 text-right">
              <div className="font-semibold">
                ${typeof order.totalAmount === 'string' 
                  ? parseFloat(order.totalAmount.replace('$', '')).toFixed(2) 
                  : order.totalAmount.toFixed(2)}
              </div>
                <select
                  value={order.status}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateOrderStatus(order._id, e.target.value);
                  }}
                  className={`px-3 py-1 rounded-full text-sm
                    ${order.status === 'Completed' ? 'bg-green-100 text-green-800' :
                    order.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                    order.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'}`}
                >
                  <option value="Pending">Pending</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-xl font-bold text-rose-800">Order Details</h3>
              <button 
                onClick={() => setSelectedOrder(null)}
                className="p-2 hover:bg-rose-100 rounded-full"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="font-semibold text-gray-600">Customer</label>
                  <p className="text-gray-800">{selectedOrder.customerName}</p>
                </div>

                <div>
                  <label className="font-semibold text-gray-600">Contact</label>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-gray-800">
                      <Mail className="h-4 w-4" />
                      {selectedOrder.customerEmail}
                    </div>
                    <div className="flex items-center gap-2 text-gray-800">
                      <Phone className="h-4 w-4" />
                      {selectedOrder.customerPhone}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="font-semibold text-gray-600">Delivery</label>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-gray-800">
                      <Calendar className="h-4 w-4" />
                      {formatDate(selectedOrder.deliveryDate)}
                    </div>
                    <div>
                      <label className="font-semibold text-gray-600">Address:</label>
                      <div className="bg-rose-50 p-3 rounded-lg mt-1">
                        <p>{selectedOrder.streetAddress}</p>
                        {selectedOrder.apartmentUnit && <p>{selectedOrder.apartmentUnit}</p>}
                        <p>{selectedOrder.city}, FL {selectedOrder.zipCode}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="font-semibold text-gray-600">Order Items</label>
                  <div className="mt-2 space-y-2">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="flex justify-between bg-rose-50 p-2 rounded">
                      <span>{item.name} × {item.quantity}</span>
                      <span className="font-semibold">
                        ${typeof item.price === 'string' 
                          ? parseFloat(item.price.replace('$', '')).toFixed(2) 
                          : item.price.toFixed(2)}
                      </span>
                    </div>
                  ))}
                  </div>
                </div>

                <div>
                  <label className="font-semibold text-gray-600">Total Amount</label>
                  <div className="flex items-center gap-2 mt-1">
                    <CreditCard className="h-4 w-4" />
                    <span className="text-xl font-bold">
                      ${typeof selectedOrder.totalAmount === 'string' 
                        ? parseFloat(selectedOrder.totalAmount.replace('$', '')).toFixed(2) 
                        : selectedOrder.totalAmount.toFixed(2)}
                    </span>
                  </div>
                </div>

                {selectedOrder.specialNotes && (
                  <div>
                    <label className="font-semibold text-gray-600">Special Notes</label>
                    <p className="mt-1 bg-rose-50 p-3 rounded">
                      {selectedOrder.specialNotes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersView